import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Tooltip from '@mui/material/Tooltip';

import FormHeader from '@webclient/components/settings/components/form-header';
import KnowledgeBaseManagement from '@webclient/components/settings/components/tabs/knowledge-base/components/management';
import { StyledScrollableTabContent } from '@webclient/components/settings/styles';

import WorkspaceProvider from '@webclient/providers/WorkspaceProvider';

import useAppDispatch from '@webclient/hooks/useAppDispatch';
import useAppSelector from '@webclient/hooks/useAppSelector';
import useResponsive from '@webclient/hooks/useResponsive';

import { setAdvancedPdfIngestionByUserId } from '@webclient/state/slices/app-settings';
import { selectAdvancedPdfIngestion } from '@webclient/state/slices/app-settings/userSettingSelector';

import ArrowLeftIcon from '~icons/knowz-iconify/arrow-left';
import HelpRoundedIcon from '~icons/knowz-iconify/help-rounded';

import { IconButton, Text } from '@knowz/knowz-ui';
import { useTranslate } from '@tolgee/react';
import PopoverProvider from '@webclient/layouts/main/sections/toolbar/components/workspaces-popover/providers/PopoverProvider';
import WorkspaceSelectionProvider from '@webclient/layouts/main/sections/toolbar/components/workspaces-popover/providers/WorkspaceSelectionProvider';
import Tabs from '@webclient/layouts/main/sections/toolbar/components/workspaces-popover/sections/content/tabs';

type Props = {
  onClose: (event: React.SyntheticEvent) => void;
};

export default function KnowledgeBase({ onClose }: Props) {
  const { t } = useTranslate();
  const isItDesktop = useResponsive('up', 'md');
  const dispatch = useAppDispatch();
  const me = useAppSelector((state) => state.auth.me);
  const advancedPdfIngestion = useAppSelector(selectAdvancedPdfIngestion);

  return (
    <WorkspaceProvider transient>
      <PopoverProvider>
        <WorkspaceSelectionProvider>
          <FormHeader
            title={t(
              'layout.main.sections.header.settings.tabs.knowledgeBase',
              'Knowledge Base',
            )}
          >
            {!isItDesktop && (
              <IconButton
                color="secondary"
                variant="ghost"
                onClick={onClose}
              >
                <ArrowLeftIcon />
              </IconButton>
            )}
          </FormHeader>

          <StyledScrollableTabContent>
            <Stack gap={4}>
              {/* Advanced PDF Ingestion toggle for alpha users */}
              {me?.alpha && (
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    gap={1}
                  >
                    <Text
                      variant="textMd"
                      weight="semibold"
                    >
                      {t(
                        'settings.knowledgeBase.advancedPdfIngestion',
                        'Advanced PDF Ingestion',
                      )}
                    </Text>
                    <Tooltip
                      title={t(
                        'settings.knowledgeBase.advancedPdfIngestion.tooltip',
                        'Warning: experimental',
                      )}
                    >
                      <HelpRoundedIcon fontSize="small" />
                    </Tooltip>
                  </Stack>
                  <Switch
                    size="small"
                    checked={advancedPdfIngestion}
                    onChange={() =>
                      dispatch(
                        setAdvancedPdfIngestionByUserId({
                          userId: me.uuid!,
                          advancedPdfIngestion: !advancedPdfIngestion,
                        }),
                      )
                    }
                  />
                </Stack>
              )}
              <Stack>
                <Text
                  variant="textMd"
                  weight="semibold"
                >
                  {t('layout.toolbar.popover.title', {
                    defaultValue: 'Select the Knowledge Base you want to use',
                  })}
                </Text>

                <Tabs />
              </Stack>

              <Divider />

              <KnowledgeBaseManagement />
            </Stack>
          </StyledScrollableTabContent>
        </WorkspaceSelectionProvider>
      </PopoverProvider>
    </WorkspaceProvider>
  );
}
